(() => {
  const reduced = matchMedia('(prefers-reduced-motion: reduce)');
  const heroes = new WeakMap();
  function initHero(slider) {
    if (heroes.has(slider)) return;
    const slides = [...slider.querySelectorAll('[data-hero-slide]')];
    const dots = [...slider.querySelectorAll('[data-hero-slide-to]')];
    if (slides.length < 2) return;
    const duration = Math.max(1000, Number(slider.dataset.duration) || 6500);
    const autoplay = slider.dataset.autoplay !== 'false';
    const pauseHover = slider.dataset.pauseHover !== 'false';
    let index = Math.max(0, slides.findIndex(slide => !slide.hidden));
    let elapsed = 0, last = performance.now(), frame, manualPause = false, hovering = false, focused = false;
    const toggle = slider.querySelector('[data-hero-toggle]');
    const paused = () => !autoplay || manualPause || hovering || focused || document.hidden || reduced.matches || slider.dataset.editorSelected === 'true';
    function show(next) {
      index = (next + slides.length) % slides.length;
      elapsed = 0;
      slides.forEach((slide, i) => {slide.hidden = i !== index; slide.classList.toggle('is-active', i === index);});
      dots.forEach((dot,i) => {dot.classList.toggle('active',i===index);dot.setAttribute('aria-current',String(i===index));});
      slider.style.setProperty('--hero-progress', reduced.matches || !autoplay ? '1' : '0');
    }
    function tick(now) {
      if (!slider.isConnected) return;
      if (!paused()) {
        elapsed += Math.min(now-last,100);
        if (elapsed >= duration) show(index+1);
      }
      last=now;
      slider.style.setProperty('--hero-progress', String(reduced.matches || !autoplay ? 1 : elapsed/duration));
      frame=requestAnimationFrame(tick);
    }
    slider.addEventListener('pointerenter',event=>{if(pauseHover && event.pointerType==='mouse') hovering=true;});
    slider.addEventListener('pointerleave',()=>{hovering=false;});
    slider.addEventListener('focusin',()=>{focused=true;});
    slider.addEventListener('focusout',event=>{focused=slider.contains(event.relatedTarget);});
    slider.addEventListener('click',event=>{
      const dot=event.target.closest('[data-hero-slide-to]'),step=event.target.closest('[data-hero-step]');
      if(dot) show(Number(dot.dataset.heroSlideTo));
      if(step) show(index+Number(step.dataset.heroStep));
      if(event.target.closest('[data-hero-toggle]')) {
        manualPause=!manualPause;
        toggle.setAttribute('aria-pressed',String(manualPause));
        toggle.setAttribute('aria-label',manualPause?'Play slideshow':'Pause slideshow');
        toggle.firstElementChild.textContent=manualPause?'▶':'Ⅱ';
      }
    });
    slider.addEventListener('keydown',event=>{if(event.key==='ArrowLeft'||event.key==='ArrowRight'){event.preventDefault();show(index+(event.key==='ArrowRight'?1:-1));}});
    heroes.set(slider,{show,destroy:()=>cancelAnimationFrame(frame)});
    frame=requestAnimationFrame(tick);
  }
  function initArrivals(root) {
    if(root.dataset.initialised) return;
    root.dataset.initialised='true';
    const tabs=[...root.querySelectorAll('[data-na-tab]')],panels=[...root.querySelectorAll('[data-na-panel]')];
    const updates=new Map();
    panels.forEach(panel=>{
      const rail=panel.querySelector('.na__swiper'),fill=panel.querySelector('.na__progress'),prev=panel.querySelector('.na__prev'),next=panel.querySelector('.na__next');
      const update=()=>{
        if(!panel.classList.contains('is-active')) return;
        const max=Math.max(0,rail.scrollWidth-rail.clientWidth),position=Math.max(0,Math.min(max,rail.scrollLeft));
        const fraction=rail.scrollWidth ? Math.min(1,(position+rail.clientWidth)/rail.scrollWidth) : 1;
        fill.style.setProperty('--rail-progress',String(fraction));
        prev.disabled=position<2;next.disabled=max-position<2;
      };
      const move=direction=>{
        const first=rail.querySelector('.swiper-slide');
        const gap=parseFloat(getComputedStyle(rail.firstElementChild).columnGap)||0;
        const step=first.getBoundingClientRect().width+gap;
        rail.scrollBy({left:direction*step,behavior:reduced.matches?'instant':'smooth'});
      };
      prev.addEventListener('click',()=>move(-1));next.addEventListener('click',()=>move(1));
      rail.addEventListener('scroll',update,{passive:true});
      new ResizeObserver(update).observe(rail);
      updates.set(panel,update);
      update();
    });
    function select(tab,focus=false) {
      tabs.forEach(t=>{const active=t===tab;t.classList.toggle('is-active',active);t.setAttribute('aria-selected',String(active));t.tabIndex=active?0:-1;});
      panels.forEach(panel=>{const active=panel.dataset.naPanel===tab.dataset.naTab;panel.classList.toggle('is-active',active);panel.hidden=!active;if(active)updates.get(panel)();});
      if(focus) tab.focus();
    }
    tabs.forEach((tab,i)=>{
      tab.addEventListener('click',()=>select(tab));
      tab.addEventListener('keydown',event=>{let next;if(event.key==='ArrowRight')next=(i+1)%tabs.length;if(event.key==='ArrowLeft')next=(i-1+tabs.length)%tabs.length;if(event.key==='Home')next=0;if(event.key==='End')next=tabs.length-1;if(next!==undefined){event.preventDefault();select(tabs[next],true);}});
    });
    if(tabs.length)select(tabs[0]);
    root.querySelectorAll('[data-na-card]').forEach(card=>{
      const main=card.querySelector('.na-card__img--main');
      if(!main)return;
      const original={src:main.src,srcset:main.getAttribute('srcset')};
      const swatches=[...card.querySelectorAll('.na-swatch')];
      const show=swatch=>{if(swatch?.dataset.naImg){main.removeAttribute('srcset');main.src=swatch.dataset.naImg;}else{main.src=original.src;if(original.srcset)main.setAttribute('srcset',original.srcset);}};
      swatches.forEach(swatch=>{
        swatch.addEventListener('pointerenter',()=>show(swatch));
        swatch.addEventListener('pointerleave',()=>show(card.querySelector('.na-swatch.is-active')));
        swatch.addEventListener('focus',()=>show(swatch));
        swatch.addEventListener('blur',()=>show(card.querySelector('.na-swatch.is-active')));
        swatch.addEventListener('click',()=>{swatches.forEach(s=>{s.classList.toggle('is-active',s===swatch);s.setAttribute('aria-pressed',String(s===swatch));});show(swatch);card.querySelectorAll('a[href]').forEach(a=>{if(!a.closest('.na-card__type'))a.href=swatch.dataset.naUrl;});});
      });
    });
  }
  const initialise=(scope=document)=>{scope.querySelectorAll('[data-hero-slider]').forEach(initHero);scope.querySelectorAll('[data-na]').forEach(initArrivals);};
  initialise();
  document.addEventListener('shopify:section:load',event=>initialise(event.target));
  document.addEventListener('shopify:block:select',event=>{const slider=event.target.closest('[data-hero-slider]');if(slider){slider.dataset.editorSelected='true';const slides=[...slider.querySelectorAll('[data-hero-slide]')];heroes.get(slider)?.show(slides.indexOf(event.target.closest('[data-hero-slide]')));}});
  document.addEventListener('shopify:block:deselect',event=>{const slider=event.target.closest('[data-hero-slider]');if(slider)delete slider.dataset.editorSelected;});
  document.addEventListener('shopify:section:unload',event=>{event.target.querySelectorAll('[data-hero-slider]').forEach(slider=>{heroes.get(slider)?.destroy();heroes.delete(slider);});});
  function updateRail(rail) {
    const fill=document.querySelector(`[data-rail-progress="${rail.id}"] [data-rail-progress-fill]`);
    if(!fill||!rail.clientWidth)return;
    fill.style.transform=`scaleX(${Math.min(1,(Math.max(0,rail.scrollLeft)+rail.clientWidth)/rail.scrollWidth)})`;
  }
  document.querySelectorAll('.product-rail').forEach(rail=>{rail.addEventListener('scroll',()=>updateRail(rail),{passive:true});new ResizeObserver(()=>updateRail(rail)).observe(rail);updateRail(rail);});
  document.addEventListener('click',event=>{
    const control=event.target.closest('[data-scroll]');
    if(control){const rail=document.getElementById(control.dataset.scroll);rail?.scrollBy({left:rail.clientWidth*.8*Number(control.dataset.direction),behavior:reduced.matches?'instant':'smooth'});}
    const room=event.target.closest('[data-room-panel]');
    if(room){const section=room.closest('[data-room-section]');section.querySelectorAll('[data-room-panel]').forEach(button=>{const active=button===room;button.classList.toggle('active',active);button.setAttribute('aria-pressed',String(active));});section.querySelectorAll('[data-room-copy]').forEach(panel=>panel.hidden=panel.dataset.roomCopy!==room.dataset.roomPanel);section.querySelectorAll('.collection-photo').forEach(panel=>panel.hidden=panel.id!==room.dataset.roomPanel);}
  });
  document.addEventListener('change',event=>{const select=event.target;if(select.matches('[data-localization],[data-sort]'))select.form.requestSubmit();if(select.matches('[data-variant-select]')){const option=select.selectedOptions[0],section=select.closest('.product-page');section.querySelector('[data-variant-price]').textContent=option.dataset.price;const compare=section.querySelector('[data-variant-compare]');compare.textContent=option.dataset.compare;compare.hidden=!option.dataset.compare;const button=select.form.querySelector('[name="add"]');button.disabled=option.dataset.available!=='true';button.textContent=button.disabled?button.dataset.soldLabel:button.dataset.addLabel;const current=new URL(location.href);current.searchParams.set('variant',select.value);history.replaceState({},'',current);}});
  document.addEventListener('keydown',event=>{if(event.key==='Escape')document.querySelectorAll('details[open]').forEach(el=>{el.open=false;el.querySelector('summary')?.focus();});});
})();
